'use client';

import React, { useState, useEffect } from 'react';
import { Navbar, Footer } from '../../components/Layout';
import { Mail, Phone, MapPin, Send, MessageSquare } from 'lucide-react';

export default function ContactUsPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [contactInfo, setContactInfo] = useState({
    email: 'care@organicheaven.com',
    phone: '+91 98765 43210',
    address: 'Pasumai Virumbi Farms,\nErode Rural, Tamil Nadu - 638001'
  });

  useEffect(() => {
    const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';
    fetch(`${authApi}/api/settings`)
      .then(res => res.json())
      .then(settings => {
        if (settings.footer_contact_details) {
          try {
            const parsed = JSON.parse(settings.footer_contact_details);
            setContactInfo({
              email: parsed.email || 'care@organicheaven.com',
              phone: parsed.phone || '+91 98765 43210',
              address: parsed.address || 'Pasumai Virumbi Farms,\nErode Rural, Tamil Nadu - 638001'
            });
          } catch (e) {
            console.error('Error parsing contact settings JSON', e);
          }
        }
      })
      .catch(err => console.error('Error fetching contact info settings:', err));
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
      setTimeout(() => setSubmitSuccess(false), 5000);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-[#F8F9F5] font-body text-text-primary flex flex-col justify-between">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12 w-full flex-grow text-left">
        {/* Page Header */}
        <div className="mb-10 border-b border-border/60 pb-6 text-center sm:text-left">
          <h1 className="text-4xl font-display font-black text-deep-green">Contact Our Farm Team</h1>
          <p className="text-text-muted text-sm font-medium mt-2">
            Have questions about our products, deliveries, or farm-to-table processes? Reach out to us anytime!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Info Columns */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white p-6 sm:p-8 rounded-3xl border border-border/40 shadow-sm space-y-6">
              <h2 className="text-2xl font-display font-bold text-deep-green">Get In Touch</h2>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-pale-green/30 rounded-xl flex items-center justify-center text-mid-green shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-black uppercase text-deep-green tracking-wider mb-0.5">Email Support</h4>
                  <p className="text-sm font-semibold text-text-muted hover:text-mid-green transition-colors">
                    {contactInfo.email}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-pale-green/30 rounded-xl flex items-center justify-center text-mid-green shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-black uppercase text-deep-green tracking-wider mb-0.5">Phone Support</h4>
                  <p className="text-sm font-semibold text-text-muted hover:text-mid-green transition-colors">
                    {contactInfo.phone}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-pale-green/30 rounded-xl flex items-center justify-center text-mid-green shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-black uppercase text-deep-green tracking-wider mb-0.5">Farm Headquarters</h4>
                  <p className="text-sm font-semibold text-text-muted leading-relaxed whitespace-pre-line">
                    {contactInfo.address}
                  </p>
                </div>
              </div>
            </div>

            {/* Google Map Mock Card */}
            <div className="bg-white p-6 rounded-3xl border border-border/40 shadow-sm overflow-hidden space-y-4">
              <div className="flex items-center gap-2 text-deep-green font-bold text-sm">
                <MapPin className="w-4.5 h-4.5 text-mid-green" /> Find us on Google Maps
              </div>
              <div className="h-48 rounded-2xl bg-surface/50 border border-border/40 relative flex items-center justify-center text-center px-4">
                <div className="space-y-1">
                  <p className="text-xs font-bold text-deep-green">Pasumai Virumbi Farms</p>
                  <p className="text-[10px] text-text-muted">Erode, Tamil Nadu (11.3410° N, 77.7172° E)</p>
                  <a 
                    href="https://maps.google.com/?q=11.3410,77.7172"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-3 px-3 py-1.5 bg-mid-green hover:bg-deep-green text-white font-bold text-[10px] uppercase tracking-wider rounded-lg transition-colors"
                  >
                    Open Directions
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form Card */}
          <div className="lg:col-span-7 bg-white p-6 sm:p-8 rounded-3xl border border-border/40 shadow-sm">
            <h2 className="text-2xl font-display font-bold text-deep-green mb-6 flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-mid-green" /> Send Us A Message
            </h2>
            
            {submitSuccess && (
              <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-xl text-sm font-semibold mb-6">
                Thank you! Your message has been sent successfully. We will get back to you within 24 hours.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-black uppercase text-deep-green tracking-wider mb-2">Your Name</label>
                <input 
                  type="text" 
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 bg-surface border border-border/60 rounded-xl text-sm font-medium focus:outline-none focus:border-mid-green/80" 
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-xs font-black uppercase text-deep-green tracking-wider mb-2">Email Address</label>
                <input 
                  type="email" 
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-surface border border-border/60 rounded-xl text-sm font-medium focus:outline-none focus:border-mid-green/80" 
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label className="block text-xs font-black uppercase text-deep-green tracking-wider mb-2">Subject</label>
                <input 
                  type="text" 
                  required
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-4 py-3 bg-surface border border-border/60 rounded-xl text-sm font-medium focus:outline-none focus:border-mid-green/80" 
                  placeholder="What is your query about?"
                />
              </div>

              <div>
                <label className="block text-xs font-black uppercase text-deep-green tracking-wider mb-2">Message</label>
                <textarea 
                  required
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-4 py-3 bg-surface border border-border/60 rounded-xl text-sm font-medium focus:outline-none focus:border-mid-green/80 resize-none" 
                  placeholder="Type your detailed message here..."
                />
              </div>

              <button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full py-4 bg-mid-green hover:bg-deep-green text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-lg shadow-mid-green/10 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span>Sending Message...</span>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
